<template>
  <form autocomplete="off">
    <md-card>
      <md-card-header
        v-if="!everythingLoaded"
        class="md-card-header-icon md-card-header-green"
      >
        <div class="card-icon">
          <md-icon class="load-rotate">hourglass_empty</md-icon>
        </div>
        <h4 class="title">Laden</h4>
      </md-card-header>
      <md-card-header
        v-if="everythingLoaded"
        class="md-card-header-icon md-card-header-green"
      >
        <div class="card-icon">
          <md-icon>date_range</md-icon>
        </div>
        <h4 class="title">
          Beschikbaarheid van chauffeur
          <u
            >{{ this.driver.firstName }}
            <template v-if="this.driver.lastNamePrefix !== ''">
              {{ this.driver.lastNamePrefix }}
            </template>
            {{ this.driver.lastName }}</u
          >
        </h4>
      </md-card-header>
      <md-card-content v-if="everythingLoaded">
        <div class="md-group">
          <md-button
            v-for="dataUniquePeriod in getDataUniquePeriods()"
            :key="dataUniquePeriod"
            :class="[
              {
                'md-success':
                  selectedPeriod === dataUniquePeriod &&
                  tableDataChanges[dataUniquePeriod] === undefined &&
                  formDataChanges[dataUniquePeriod] === undefined
              },
              {
                'md-primary':
                  selectedPeriod === dataUniquePeriod &&
                  (tableDataChanges[dataUniquePeriod] !== undefined ||
                    formDataChanges[dataUniquePeriod] !== undefined)
              },
              {
                'md-warning':
                  selectedPeriod !== dataUniquePeriod &&
                  (tableDataChanges[dataUniquePeriod] !== undefined ||
                    formDataChanges[dataUniquePeriod] !== undefined)
              },
              'md-sm'
            ]"
            v-on:click="changeSelectedPeriod(dataUniquePeriod)"
            >{{ getPeriodLabel(dataUniquePeriod) }}
          </md-button>
          <md-button
            class="md-just-icon"
            v-on:click="handleAddNew()"
            :disabled="formTouched || requesting"
            v-bind:class="{
              'md-success': !formTouched,
              'md-warning': formTouched
            }"
          >
            <md-icon>add</md-icon>
          </md-button>
        </div>
        <br />
        <md-button
          class="md-warning"
          v-on:click="handleDelete(selectedPeriod)"
          v-if="getDataUniquePeriods().length > 1"
          :disabled="requesting || selectedPeriod === 'new|new'"
          >Verwijder geselecteerde periode
        </md-button>

        <ValidationProvider
          name="startDate"
          :rules="{
            required: false,
            regex: /^([0-2]{1}[0-9]{1}|3[0-1]{1})-(0[0-9]{1}|1[0-2]{1})-20[0-9]{2}$/
          }"
          v-slot="{ passed, failed }"
        >
          <md-field
            class="md-datepicker"
            :class="[{ 'md-error': failed }, { 'md-valid': passed }]"
            :disabled="requesting"
          >
            <label>Start datum</label>
            <md-datepicker
              v-model="startDate"
              name="startDate"
              id="startDate"
              :md-model-type="String"
              md-immediately
            />

            <slide-y-down-transition>
              <md-icon class="error" v-show="failed">close</md-icon>
            </slide-y-down-transition>
            <slide-y-down-transition>
              <md-icon class="success" v-show="passed">done</md-icon>
            </slide-y-down-transition>
          </md-field>
        </ValidationProvider>

        <ValidationProvider
          name="stopDate"
          :rules="{
            required: false,
            regex: /^([0-2]{1}[0-9]{1}|3[0-1]{1})-(0[0-9]{1}|1[0-2]{1})-20[0-9]{2}$/
          }"
          v-slot="{ passed, failed }"
        >
          <md-field
            class="md-datepicker"
            :class="[{ 'md-error': failed }, { 'md-valid': passed }]"
            :disabled="requesting"
          >
            <label>Stop datum</label>
            <md-datepicker
              v-model="stopDate"
              :md-model-type="String"
              md-immediately
            />

            <slide-y-down-transition>
              <md-icon class="error" v-show="failed">close</md-icon>
            </slide-y-down-transition>
            <slide-y-down-transition>
              <md-icon class="success" v-show="passed">done</md-icon>
            </slide-y-down-transition>
          </md-field>
        </ValidationProvider>

        <md-switch v-model="orderAvailabilityByRoute" :disabled="formTouched">
          Gesorteerd op
          <template v-if="orderAvailabilityByRoute">route</template>
          <template v-if="!orderAvailabilityByRoute">dienst</template>
          <template v-if="formTouched"> (Sla wijzigingen eerst op)</template>
        </md-switch>

        <div class="scrollContainer">
          <table
            class="striped contrast"
            v-for="(weekOddEven, weekOddEvenIndex) in tableData[selectedPeriod]"
            :key="weekOddEvenIndex"
          >
            <tr>
              <th rowspan="2">
                {{ getWeeksOddEven()[weekOddEvenIndex].label }}
              </th>
              <template
                v-for="(tableHeader, tableHeaderIndex) in getTableHeaders"
                ><th
                  v-if="tableHeader.routeColums > 0"
                  v-html="tableHeader.routes.replaceAll(',', ',<br/>')"
                  :key="tableHeaderIndex"
                  :colspan="tableHeader.routeColums"
                  :class="tableHeader.class"
                ></th
              ></template>
            </tr>
            <tr>
              <template
                v-for="(tableHeader, tableHeaderIndex) in getTableHeaders"
                ><th
                  v-if="tableHeader.shiftColums > 0"
                  v-html="tableHeader.shifts"
                  :key="tableHeaderIndex"
                  :colspan="tableHeader.shiftColums"
                  :class="tableHeader.class"
                ></th
              ></template>
            </tr>
            <tr
              v-for="(tableRow, tableRowIndex) in tableData[selectedPeriod][
                weekOddEvenIndex
              ]"
              :key="tableRowIndex"
            >
              <td>{{ daysOfTheWeek[tableRowIndex] }}</td>
              <td
                v-for="(tableRowColumn, tableRowColumnIndex) in tableRow"
                :key="tableRowColumnIndex"
                :class="
                  tableData[selectedPeriod][weekOddEvenIndex][tableRowIndex][
                    tableRowColumnIndex
                  ].class
                "
              >
                <button
                  class="availability-status"
                  v-on:click="
                    loopThroughStatusCodes(
                      weekOddEvenIndex,
                      tableRowIndex,
                      tableRowColumnIndex
                    )
                  "
                  onclick="return false;"
                  :title="
                    getLabelByStatus(
                      tableData[selectedPeriod][weekOddEvenIndex][
                        tableRowIndex
                      ][tableRowColumnIndex].availability
                    )
                  "
                >
                  <md-icon
                    :style="
                      getIconStyleColorByStatus(
                        tableRow[tableRowColumnIndex].availability
                      )
                    "
                    >{{
                      getIconByStatus(
                        tableRow[tableRowColumnIndex].availability
                      )
                    }}</md-icon
                  >
                </button>
              </td>
            </tr>
          </table>
        </div>

        <ul class="legend">
          <li><h5>Legenda</h5></li>
          <li
            v-for="(status, statusIndex) in selectableAvailabilityTypes"
            :key="statusIndex"
          >
            <md-icon :style="getIconStyleColorByStatus(statusIndex)"
              >{{ getIconByStatus(statusIndex) }}
            </md-icon>
            {{ status.label }}
          </li>
        </ul>
      </md-card-content>
      <md-card-actions>
        <md-button
          native-type="button"
          @click.native.prevent="goBack"
          :disabled="requesting"
          v-bind:class="{
            'md-primary': !formTouched,
            'md-warning': formTouched
          }"
        >
          Terug
        </md-button>
        <md-button
          :disabled="!formTouched || requesting"
          native-type="submit"
          @click.native.prevent="validate"
          class="md-success"
          >Opslaan
        </md-button>
      </md-card-actions>
    </md-card>
  </form>
</template>

<script>
import sweetAlert from "sweetalert2";

export default {
  data() {
    return {
      driver: {
        id: "",
        firstName: "",
        lastNamePrefix: "",
        lastName: "",
        email: ""
      },
      daysOfTheWeek: {
        1: "Maandag",
        2: "Dinsdag",
        3: "Woensdag",
        4: "Donderdag",
        5: "Vrijdag",
        6: "Zaterdag",
        7: "Zondag"
      },
      loaded: {
        driver: false,
        groups: false,
        routes: false,
        shifts: false,
        shiftRoutes: false,
        availablePeriods: false,
        selectableAvailabilityTypes: false
      },
      groups: [],
      routes: [],
      shifts: [],
      availablePeriods: [],
      selectableAvailabilityTypes: [],
      requesting: false,
      selectedPeriod: null,
      startDate: null,
      stopDate: null,
      formDataChanges: {},
      tableData: {},
      tableDataChanges: {},
      touched: {
        startDate: false,
        stopDate: false,
        tableData: false
      },
      groupsToMergeRoutes: [],
      orderAvailabilityByRoute: false
    };
  },
  computed: {
    everythingLoaded: function() {
      for (let i in this.loaded) {
        if (!this.loaded[i]) {
          return false;
        }
      }

      return true;
    },
    dataUniquePeriods: function() {
      return this.getDataUniquePeriods();

      let periods = [];

      if (this.availablePeriods === undefined) {
        return periods;
      }

      for (var i in this.availablePeriods) {
        if (this.availablePeriods[i].id.length !== 36) {
          continue;
        }

        if (
          periods.indexOf(
            this.availablePeriods[i].startDate +
              "|" +
              this.availablePeriods[i].stopDate
          ) !== -1
        ) {
          continue;
        }

        let period =
          this.availablePeriods[i].startDate +
          "|" +
          this.availablePeriods[i].stopDate;

        periods.push(period);
      }

      if (this.tableData["new|new"] !== undefined) {
        periods.push("new|new");
      }

      return periods.sort((a, b) => {
        if (a.substring(0, 4) === "null") {
          return -1;
        } else if (b.substring(0, 4) === "null") {
          return 1;
        }

        if (a > b) {
          return 1;
        } else if (a < b) {
          return -1;
        }
        return 0;
      });
    },
    getTableHeaders: function() {
      let tableHeaders = [];

      for (let g in this.groups) {
        let headersByRoute = {};
        for (let s in this.shifts) {
          let headers = [];
          let headersToMerge = [];
          let combinedHeaders = "";
          for (let r in this.routes) {
            if (
              this.routes[r].routegroups_id !== this.groups[g].id ||
              this.shifts[s].routeIds.indexOf(this.routes[r].id) === -1
            ) {
              continue;
            }

            if (
              this.groupsToMergeRoutes.indexOf(this.groups[g].id) > -1 ||
              this.routesToMerge.indexOf(this.routes[r].id) > -1
            ) {
              headersToMerge.push(this.getRouteName(this.routes[r].id));
              if (headers.indexOf("MERGED_DATA:" + this.shifts[s].id) === -1) {
                headers.push("MERGED_DATA:" + this.shifts[s].id);
              }
            } else {
              if (this.orderAvailabilityByRoute === true) {
                if (headersByRoute[this.routes[r].order] === undefined) {
                  headersByRoute[this.routes[r].order] = [];
                }
                headersByRoute[this.routes[r].order].push(
                  this.getRouteName(this.routes[r].id) +
                    "\n" +
                    this.shifts[s].name
                );
              } else {
                headers.push(
                  this.getRouteName(this.routes[r].id) +
                    "\n" +
                    this.shifts[s].name
                );
              }
            }
          }

          if (headersToMerge.length > 0) {
            for (let i in headersToMerge) {
              if (combinedHeaders !== "") {
                combinedHeaders += ", ";
              }
              combinedHeaders += headersToMerge[i];
            }
            if (combinedHeaders !== "") {
              combinedHeaders += "\n" + this.shifts[s].name;
              headersToMerge = [];
              if (this.orderAvailabilityByRoute === true) {
                if (headersByRoute[0] === undefined) {
                  headersByRoute[0] = [];
                }
                headersByRoute[0].push(combinedHeaders);
              } else {
                if (headers.indexOf("MERGED_DATA:" + this.shifts[s].id) > -1) {
                  headers[
                    headers.indexOf("MERGED_DATA:" + this.shifts[s].id)
                  ] = combinedHeaders;
                } else {
                  headers.push(combinedHeaders);
                }
              }
            }
          }
          if (this.orderAvailabilityByRoute === false) {
            let headerCounter = 1;
            for (let i in headers) {
              tableHeaders.push({
                routes: headers[i].split("\n")[0],
                routeColums: 1,
                shifts: headers[i].split("\n")[1],
                shiftColums: 1,
                class:
                  headerCounter === 1 && tableHeaders.length > 0
                    ? "contrast"
                    : ""
              });
              headerCounter++;
            }
          }
        }

        if (this.orderAvailabilityByRoute === true) {
          for (let o in headersByRoute) {
            var headerCounter = 1;
            for (let i in headersByRoute[o]) {
              tableHeaders.push({
                routes: headersByRoute[o][i].split("\n")[0],
                routeColums: 1,
                shifts: headersByRoute[o][i].split("\n")[1],
                shiftColums: 1,
                class:
                  headerCounter === 1 && tableHeaders.length > 0
                    ? "contrast"
                    : ""
              });
              headerCounter++;
            }
          }
        }
      }

      if (this.orderAvailabilityByRoute === true) {
        let firstHeaderText = "";
        let firstHeaderIndex = 0;
        for (let tableHeadersIndex in tableHeaders) {
          if (tableHeaders[tableHeadersIndex].routes !== firstHeaderText) {
            firstHeaderText = tableHeaders[tableHeadersIndex].routes;
            firstHeaderIndex = tableHeadersIndex;
            continue;
          }

          tableHeaders[firstHeaderIndex].routeColums++;
          tableHeaders[tableHeadersIndex].routeColums = 0;
        }
      } else {
        let firstHeaderText = "";
        let firstHeaderIndex = 0;
        for (let tableHeadersIndex in tableHeaders) {
          if (tableHeaders[tableHeadersIndex].shifts !== firstHeaderText) {
            firstHeaderText = tableHeaders[tableHeadersIndex].shifts;
            firstHeaderIndex = tableHeadersIndex;

            continue;
          }

          tableHeaders[firstHeaderIndex].shiftColums++;
          tableHeaders[tableHeadersIndex].shiftColums = 0;
        }
      }

      return tableHeaders;
    },
    getStartDateFromSelectedPeriod: function() {
      return this.getStartDateFromPeriod(this.selectedPeriod);
    },
    getStopDateFromSelectedPeriod: function() {
      return this.getStopDateFromPeriod(this.selectedPeriod);
    },
    formTouched: function() {
      for (var i in this.touched) {
        if (this.touched[i]) {
          return true;
        }
      }

      return false;
    },
    routesToMerge: function() {
      let routeIds = [];

      for (let r in this.routes) {
        if (this.routes[r].mergeInSimpleAvailability !== 1) {
          continue;
        }
        routeIds.push(this.routes[r].id);
      }

      return routeIds;
    }
  },
  methods: {
    init() {
      this.$material.locale.dateFormat = "dd-MM-yyyy";
      this.$material.locale.firstDayOfAWeek = 1;
      this.$material.locale.days = [
        "Zondag",
        "Maandag",
        "Dinsdag",
        "Woensdag",
        "Donderdag",
        "Vrijdag",
        "Zaterdag"
      ];
      this.$material.locale.shortDays = [
        "Zon",
        "Maa",
        "Din",
        "Woe",
        "Don",
        "Vri",
        "Zat"
      ];
      this.$material.locale.shorterDays = ["Z", "M", "D", "W", "D", "V", "Z"];
      this.$material.locale.months = [
        "Januari",
        "Februari",
        "Maart",
        "April",
        "Mei",
        "Juni",
        "Juli",
        "Augustus",
        "September",
        "Oktober",
        "November",
        "December"
      ];
      this.$material.locale.shortMonths = [
        "Jan",
        "Feb",
        "Mrt",
        "Apr",
        "Mei",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Okt",
        "Nov",
        "Dec"
      ];
      this.$material.locale.shorterMonths = [
        "J",
        "F",
        "M",
        "A",
        "M",
        "J",
        "J",
        "A",
        "S",
        "O",
        "N",
        "D"
      ];
    },
    loadRouteGroups() {
      this.$http.get(this.$apiBaseUrl + "routes/groups").then(
        response => {
          this.loaded.groups = true;
          this.groups = response.data.routeGroups;
        },
        response => {}
      );
    },
    loadRoutes() {
      this.$http.get(this.$apiBaseUrl + "routes").then(
        response => {
          let routes = response.data.routes;

          for (var i = 0; i < routes.length; i++) {
            routes[i].order = routes[i].order.toString();
            routes[i].isreserve = routes[i].isreserve.toString();
            routes[i].shiftIds = [];

            if (this.selectedRouteId === null) {
              this.selectedRouteId = routes[i].id;
            }
          }

          this.loaded.routes = true;
          this.routes = routes;
        },
        response => {
          if (response.status === 403) {
            this.notifyVue(
              "U heeft geen rechten voor het beheren van routes",
              "add_alert",
              "danger",
              0
            );
          }

          return [];
        }
      );
    },
    loadShifts() {
      this.$http.get(this.$apiBaseUrl + "shifts").then(
        response => {
          let shifts = this.sortCollection(
            response.data.shifts,
            "order",
            "asc"
          );

          for (let i in shifts) {
            shifts[i]["routeIds"] = [];
          }

          this.loaded.shifts = true;
          this.shifts = shifts;
          this.loadShiftsRoutes();
        },
        response => {
          if (response.status === 403) {
            this.notifyVue(
              "U heeft geen rechten voor het beheren van diensten",
              "add_alert",
              "danger",
              0
            );
          }

          return [];
        }
      );
    },
    loadShiftsRoutes() {
      this.$http.get(this.$apiBaseUrl + "shifts/routes").then(
        response => {
          let routeIds = {};
          for (let s in this.shifts) {
            routeIds[this.shifts[s].id] = [];
          }

          for (let shiftId in response.data.shifts_routes) {
            for (let i in response.data.shifts_routes[shiftId]) {
              routeIds[shiftId].push(
                response.data.shifts_routes[shiftId][i].id
              );
            }
          }

          for (let s in this.shifts) {
            this.shifts[s]["routeIds"] = routeIds[this.shifts[s].id];
          }

          this.loaded.shiftRoutes = true;
        },
        response => {
          if (response.status === 403) {
            this.notifyVue(
              "U heeft geen rechten voor het beheren van diensten",
              "add_alert",
              "danger",
              0
            );
          }

          return [];
        }
      );
    },
    loadDriver(id) {
      this.requesting = true;
      this.$http.get(this.$apiBaseUrl + "drivers/" + id).then(
        response => {
          this.driver.firstName = response.data.driver.firstName;
          this.driver.lastNamePrefix = response.data.driver.lastNamePrefix;
          this.driver.lastName = response.data.driver.lastName;
          this.driver.email = response.data.driver.email;

          this.requesting = false;
          this.loaded.driver = true;
        },
        response => {
          this.requesting = false;
          if (response.status === 403) {
            // this.notifyVue("U heeft geen rechten voor het beheren van chauffeurs", 'add_alert', 'danger', 0);
          }
        }
      );
    },
    loadAvailablePeriods(userId) {
      this.loaded.availablePeriods = false;
      this.$http
        .get(this.$apiBaseUrl + "drivers/" + userId + "/availableperiods")
        .then(
          response => {
            let availablePeriods = [];
            for (let i in response.data.driverPeriods) {
              if (response.data.driverPeriods[i].shift_ids.length === 0) {
                continue;
              }

              availablePeriods.push(response.data.driverPeriods[i]);
            }
            this.availablePeriods = availablePeriods;
            this.loaded.availablePeriods = true;
          },
          response => {
            if (response.status === 403) {
              this.notifyVue(
                "U heeft geen rechten voor het beheren van beschikbaarheidsperioden",
                "add_alert",
                "danger",
                0
              );
            }

            return [];
          }
        );
    },
    loadSelectableAvailabilityTypes() {
      this.$http
        .get(this.$apiBaseUrl + "drivers/selectableavailabilitytypes")
        .then(
          response => {
            this.loaded.selectableAvailabilityTypes = true;
            this.selectableAvailabilityTypes =
              response.data.selectableAvailabilityTypes;
          },
          response => {
            if (response.status === 403) {
              this.notifyVue(
                "U heeft geen rechten voor het beheren van beschikbaarheidsperioden",
                "add_alert",
                "danger",
                0
              );
            }

            return [];
          }
        );
    },
    getRouteName(routeId) {
      for (let r in this.routes) {
        if (this.routes[r].id === routeId) {
          return this.routes[r].name;
        }
      }
      return "";
    },
    getRouteGroupId(routeId) {
      for (let r in this.routes) {
        if (this.routes[r].id === routeId) {
          return this.routes[r].routegroups_id;
        }
      }
      return "";
    },
    getAvailability(
      startDate,
      stopDate,
      weekOddEven,
      dayOfTheWeek,
      shiftId,
      routeIds
    ) {
      for (let a in this.availablePeriods) {
        let allRoutesFound = true;
        for (let r in routeIds) {
          if (this.availablePeriods[a].route_ids.indexOf(routeIds[r]) === -1) {
            allRoutesFound = false;
            break;
          }
        }

        if (allRoutesFound === false) {
          continue;
        }

        if (
          this.availablePeriods[a].startDate === startDate &&
          this.availablePeriods[a].stopDate === stopDate &&
          this.availablePeriods[a].weekOddEven === weekOddEven &&
          this.availablePeriods[a].dayOfTheWeek === dayOfTheWeek &&
          this.availablePeriods[a].shift_ids.indexOf(shiftId) >= 0
        ) {
          return this.availablePeriods[a].availability;
        }
      }

      return 9;
    },
    getWeeksOddEven() {
      return [
        {
          id: 0,
          name: "even",
          label: "Even weken"
        },
        {
          id: 1,
          name: "odd",
          label: "Oneven weken"
        }
      ];
    },
    goBack() {
      if (!this.formTouched) {
        this.$router.go(-1);
        return;
      }

      this.showSweetAlertGoBack();
    },
    validateDates() {
      let msg = "";

      // check uses of empty dates
      let startDatesEmpty = 0;
      let stopDatesEmpty = 0;
      let dataUniquePeriods = this.getDataUniquePeriods();
      for (let p in dataUniquePeriods) {
        if (
          this.formDataChanges[dataUniquePeriods[p]] !== undefined &&
          this.formDataChanges[dataUniquePeriods[p]].startDate !== undefined
        ) {
          if (this.formDataChanges[dataUniquePeriods[p]].startDate == null) {
            startDatesEmpty++;
          }
        } else if (dataUniquePeriods[p].split("|")[0] === "null") {
          startDatesEmpty++;
        }

        if (
          this.formDataChanges[dataUniquePeriods[p]] !== undefined &&
          this.formDataChanges[dataUniquePeriods[p]].stopDate !== undefined
        ) {
          if (this.formDataChanges[dataUniquePeriods[p]].stopDate == null) {
            stopDatesEmpty++;
          }
        } else if (dataUniquePeriods[p].split("|")[1] === "null") {
          stopDatesEmpty++;
        }
      }

      if (startDatesEmpty > 1) {
        msg +=
          "<br/>\n&bull;&nbsp;Start datum kan maar in 1 periode leeg zijn.";
      }

      if (stopDatesEmpty > 1) {
        msg += "<br/>\n&bull;&nbsp;Stop datum kan maar in 1 periode leeg zijn.";
      }

      // Check periods don't overlap
      let prevStopDate = null;
      for (let p in dataUniquePeriods) {
        let startDate =
          this.formDataChanges[dataUniquePeriods[p]] !== undefined &&
          this.formDataChanges[dataUniquePeriods[p]].startDate !== undefined
            ? this.formDataChanges[dataUniquePeriods[p]].startDate
            : this.getStartDateFromPeriod(dataUniquePeriods[p]);
        let stopDate =
          this.formDataChanges[dataUniquePeriods[p]] !== undefined &&
          this.formDataChanges[dataUniquePeriods[p]].stopDate !== undefined
            ? this.formDataChanges[dataUniquePeriods[p]].stopDate
            : this.getStopDateFromPeriod(dataUniquePeriods[p]);

        if (startDate > stopDate) {
          msg +=
            "<br/>\n&bull;&nbsp;Start datum " +
            this.formatDate(startDate) +
            " komt na " +
            this.formatDate(stopDate) +
            ".";
        }

        if (p > 0 && prevStopDate >= startDate) {
          msg +=
            "<br/>\n&bull;&nbsp;Stop datum " +
            this.formatDate(prevStopDate) +
            " komt na de start datum " +
            this.formatDate(startDate) +
            " van de volgende periode. Perioden moeten na elkaar komen zonder overlap.";
        }

        prevStopDate = stopDate;
      }

      if (msg !== "") {
        msg =
          "Fout(en) geconstateerd tijdens het opslaan van beschikbaarheid:" +
          msg;
        this.notifyVue(msg, "add_alert", "danger", 0);

        return false;
      }

      return true;
    },
    validate() {
      if (this.validateDates() === false) {
        return;
      }

      let updates = [];

      if (Object.keys(this.formDataChanges).length > 0) {
        for (let period in this.formDataChanges) {
          let startDate = this.getStartDateFromPeriod(period);
          let stopDate = this.getStopDateFromPeriod(period);

          for (let a in this.availablePeriods) {
            if (
              this.availablePeriods[a].startDate !== startDate ||
              this.availablePeriods[a].stopDate !== stopDate
            ) {
              continue;
            }

            let availablePeriod = structuredClone(this.availablePeriods[a]);
            availablePeriod.period = period;

            for (let field in this.formDataChanges[period]) {
              availablePeriod[field] = this.formDataChanges[period][field];
            }

            if (availablePeriod.shift_ids.length === 0) {
              // Left over availability for disabled shifts and/or routes
              continue;
            }
            updates.push(availablePeriod);
          }
        }
      }

      if (Object.keys(this.tableDataChanges).length > 0) {
        for (let period in this.tableDataChanges) {
          for (let weekOddEven in this.tableDataChanges[period]) {
            for (let dayOfTheWeek in this.tableDataChanges[period][
              weekOddEven
            ]) {
              for (let shiftId in this.tableDataChanges[period][weekOddEven][
                dayOfTheWeek
              ]) {
                let routeIds = [];
                routeIds = shiftId.split("|")[0].split(",");
                shiftId = shiftId.split("|")[1];
                let shiftFound = false;
                for (let rIdIndex in routeIds) {
                  let routeId = routeIds[rIdIndex];
                  for (let u in updates) {
                    if (
                      updates[u].period !== period ||
                      updates[u].weekOddEven !== parseInt(weekOddEven) ||
                      updates[u].dayOfTheWeek !== parseInt(dayOfTheWeek) ||
                      updates[u].shift_ids.indexOf(shiftId) === -1 ||
                      updates[u].route_ids.indexOf(routeId) === -1 ||
                      (updates[u].route_ids.indexOf(routeId) > -1 &&
                        updates[u].route_ids.length > 1)
                    ) {
                      continue;
                    }
                    shiftFound = true;
                    updates[u].availability = this.tableDataChanges[period][
                      weekOddEven
                    ][dayOfTheWeek][routeIds.join(",") + "|" + shiftId];
                  }

                  if (shiftFound === false) {
                    for (let a in this.availablePeriods) {
                      if (
                        this.availablePeriods[a].weekOddEven !==
                          parseInt(weekOddEven) ||
                        this.availablePeriods[a].dayOfTheWeek !==
                          parseInt(dayOfTheWeek) ||
                        this.availablePeriods[a].shift_ids.indexOf(shiftId) ===
                          -1 ||
                        this.availablePeriods[a].route_ids.indexOf(routeId) ===
                          -1 ||
                        this.availablePeriods[a].startDate !==
                          this.getStartDateFromPeriod(period) ||
                        this.availablePeriods[a].stopDate !==
                          this.getStopDateFromPeriod(period)
                      ) {
                        continue;
                      }
                      shiftFound = true;

                      let availablePeriod = structuredClone(
                        this.availablePeriods[a]
                      );
                      availablePeriod.period = period;
                      let routeIdIndex = availablePeriod.route_ids.indexOf(
                        routeId
                      );
                      if (
                        routeIdIndex > -1 &&
                        availablePeriod.route_ids.length > 1 &&
                        this.groupsToMergeRoutes.indexOf(
                          this.getRouteGroupId(routeId)
                        ) === -1 &&
                        this.routesToMerge.indexOf(routeId) === -1
                      ) {
                        let updateIndex = this.availabilityIdExistInUpdates(
                          availablePeriod.id,
                          updates
                        );
                        if (updateIndex > -1) {
                          availablePeriod = updates[updateIndex];
                          routeIdIndex = availablePeriod.route_ids.indexOf(
                            routeId
                          );
                        }
                        availablePeriod.route_ids.splice(routeIdIndex, 1);

                        for (let x in this.availablePeriods) {
                          if (
                            this.availablePeriods[x].id !== availablePeriod.id
                          ) {
                            continue;
                          }

                          availablePeriod.availability = this.availablePeriods[
                            x
                          ].availability;
                        }

                        let availablePeriodNew = structuredClone(
                          availablePeriod
                        );
                        availablePeriodNew.id = null;
                        availablePeriodNew.shift_ids = [shiftId];
                        availablePeriodNew.route_ids = routeIds;

                        availablePeriodNew.availability = this.tableDataChanges[
                          period
                        ][weekOddEven][dayOfTheWeek][
                          routeIds.join(",") + "|" + shiftId
                        ];

                        if (updateIndex > -1) {
                          updates[updateIndex] = availablePeriod;
                        } else {
                          updates.push(availablePeriod);
                        }
                        updates.push(availablePeriodNew);
                      } else {
                        if (
                          availablePeriod.route_ids.length === routeIds.length
                        ) {
                          availablePeriod.availability = this.tableDataChanges[
                            period
                          ][weekOddEven][dayOfTheWeek][
                            routeIds.join(",") + "|" + shiftId
                          ];
                          updates.push(availablePeriod);
                        } else {
                          let availablePeriodNew = structuredClone(
                            availablePeriod
                          );
                          availablePeriodNew.id = null;
                          availablePeriodNew.shift_ids = [shiftId];
                          availablePeriodNew.route_ids = routeIds;

                          availablePeriodNew.availability = this.tableDataChanges[
                            period
                          ][weekOddEven][dayOfTheWeek][
                            routeIds.join(",") + "|" + shiftId
                          ];

                          availablePeriod.route_ids = this.arrayRemoveValues(
                            availablePeriod.route_ids,
                            routeIds
                          );

                          updates.push(availablePeriod);
                          updates.push(availablePeriodNew);
                        }
                      }
                    }
                  }

                  if (shiftFound === false) {
                    let forRouteIds = [];

                    forRouteIds.push(routeId);

                    let availablePeriod = {
                      availability: this.tableDataChanges[period][weekOddEven][
                        dayOfTheWeek
                      ][routeIds.join(",") + "|" + shiftId],
                      dayOfTheWeek: parseInt(dayOfTheWeek),
                      driver_id: this.$route.params.userId,
                      route_ids: forRouteIds,
                      shift_ids: [shiftId],
                      startDate:
                        this.formDataChanges[period] !== undefined &&
                        this.formDataChanges[period].startDate !== undefined
                          ? this.formDataChanges[period].startDate
                          : this.getStartDateFromPeriod(period),
                      stopDate:
                        this.formDataChanges[period] !== undefined &&
                        this.formDataChanges[period].stopDate !== undefined
                          ? this.formDataChanges[period].stopDate
                          : this.getStopDateFromPeriod(period),
                      weekOddEven: parseInt(weekOddEven),
                      period: period
                    };

                    updates.push(availablePeriod);
                  }
                }
              }
            }
          }
        }
      }

      updates = this.removeNewUnavailablePeriods(updates);

      let newItemUpdatesFound = false;
      for (let u in updates) {
        if (
          newItemUpdatesFound === false &&
          (updates[u].id === undefined || updates[u].id === null)
        ) {
          newItemUpdatesFound = true;
        }

        if (
          newItemUpdatesFound === false ||
          updates[u].id === undefined ||
          updates[u].id === null
        ) {
          continue;
        }

        let update = updates[u];
        updates.splice(u, 1);
        updates.unshift(update);
      }

      this.requesting = true;
      this.$http
        .post(
          this.$apiBaseUrl +
            "drivers/" +
            this.$route.params.userId +
            "/availableperiods",
          Object.assign({ availableperiods: updates })
        )
        .then(
          response => {
            this.requesting = false;
            setTimeout(this.resetTouched, 100);
            this.notifyVue(
              "Beschikbaarheid succesvol opgeslagen",
              "check",
              "success",
              5000
            );
            this.resetAfterSave();
          },
          response => {
            if (response.status === 422) {
              this.requesting = false;
              let msg =
                "Fout geconstateerd tijdens opslaan van beschikbaarheid:";

              for (var i in response.data.errors) {
                msg += "<br/>\n&bull;&nbsp;" + response.data.errors[i];
              }

              this.notifyVue(msg, "add_alert", "danger", 0);
            } else {
              this.notifyVue(
                "Onbekende fout tijdens opslaan van beschikbaarheid",
                "report_problem",
                "danger",
                0
              );
            }
          }
        );
    },
    availabilityIdExistInUpdates(id, updates) {
      for (let u in updates) {
        if (updates[u].id === id) {
          return u;
        }
      }

      return -1;
    },
    removeNewUnavailablePeriods(updates) {
      let updateRemoved = true;
      while (updateRemoved) {
        updateRemoved = false;
        for (let u in updates) {
          if (
            updates[u].availability === 9 &&
            (updates[u].id === undefined ||
              updates[u].id === "" ||
              updates[u].id === null)
          ) {
            updates.splice(u, 1);
            updateRemoved = true;
            break;
          }
        }
      }

      return updates;
    },
    resetAfterSave() {
      this.resetTouched();
      this.loadAvailablePeriods(this.$route.params.userId);
      this.formDataChanges = {};
      this.tableDataChanges = {};
      this.selectedPeriod = null;
    },
    resetAfterDelete() {
      this.resetTouched();
      this.loadAvailablePeriods(this.$route.params.userId);
      this.formDataChanges = {};
      this.tableDataChanges = {};
      this.selectedPeriod = null;
    },
    handleAddNew() {
      if (this.selectedPeriod === "null|null") {
        this.notifyVue(
          "Geef de bestaande periode zonder stop of start datum een start of stop datum.",
          "add_alert",
          "danger",
          0
        );

        return;
      }

      if (this.getDataUniquePeriods().length === 0) {
        this.handleAddNewCreateData();
        return;
      }

      sweetAlert
        .fire({
          title: "Beschikbaarheid kopiëren?",
          text:
            "Wilt u de beschikbaarheid van de huidige periode kopiëren naar de nieuwe periode?",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Ja, kopiëren",
          cancelButtonText: "Nee, niet kopiëren",
          customClass: {
            confirmButton: "md-button md-success",
            cancelButton: "md-button md-warning"
          },
          buttonsStyling: false
        })
        .then(result => {
          if (result.value) {
            this.handleAddNewCopyData(this.selectedPeriod);
          } else if (result.dismiss === sweetAlert.DismissReason.cancel) {
            this.handleAddNewCreateData();
          }
        });
    },
    handleAddNewCopyData(copyFromPeriod) {
      let period = "new|new";
      this.tableDataChanges[period] = {};
      this.tableData[period] = {};

      for (let w in this.tableData[copyFromPeriod]) {
        this.tableDataChanges[period][w] = {};
        this.tableData[period][w] = {};

        for (let d in this.tableData[copyFromPeriod][w]) {
          this.tableDataChanges[period][w][d] = {};
          this.tableData[period][w][d] = {};
          for (let x in this.tableData[copyFromPeriod][w][d]) {
            this.tableDataChanges[period][w][d][x] = this.tableData[
              copyFromPeriod
            ][w][d][x].availability;

            this.tableData[period][w][d][x] = {};
            this.tableData[period][w][d][x].label = this.tableData[
              copyFromPeriod
            ][w][d][x].label;
            this.tableData[period][w][d][x].availability = this.tableData[
              copyFromPeriod
            ][w][d][x].availability;
          }
        }
      }

      this.formDataChanges[period] = {};
      if (this.stopDate === null) {
        this.formDataChanges[period].startDate = null;
        this.startDate = null;
      } else {
        let stopDate = new Date(this.formatDateBack(this.stopDate));

        this.formDataChanges[period].startDate =
          stopDate.addDays(1).getUTCDate() +
          "-" +
          (stopDate.addDays(1).getUTCMonth() + 1) +
          "-" +
          stopDate.addDays(1).getUTCFullYear();
        this.startDate = this.formDataChanges[period].startDate;
      }
      this.formDataChanges[period].stopDate = null;
      this.stopDate = null;

      this.touched.tableData = true;

      this.selectedPeriod = period;
    },
    handleAddNewCreateData() {
      let availability = 9;

      let period = "new|new";

      this.tableDataChanges[period] = {};

      this.tableData[period] = {};
      for (let w in this.getWeeksOddEven()) {
        this.tableData[period][w] = {};
        this.tableDataChanges[period][w] = {};
        for (let d in this.daysOfTheWeek) {
          this.tableData[period][w][d] = {};
          this.tableDataChanges[period][w][d] = {};
          for (let g in this.groups) {
            let headersByRoute = {};
            for (let s in this.shifts) {
              let mergeRoutes = [];
              for (let r in this.routes) {
                if (
                  this.routes[r].routegroups_id !== this.groups[g].id ||
                  this.shifts[s].routeIds.indexOf(this.routes[r].id) === -1
                ) {
                  continue;
                }

                if (
                  this.groupsToMergeRoutes.indexOf(this.groups[g].id) === -1 &&
                  this.routesToMerge.indexOf(this.routes[r].id) === -1
                ) {
                  if (this.orderAvailabilityByRoute === true) {
                    if (headersByRoute[this.routes[r].order] === undefined) {
                      headersByRoute[this.routes[r].order] = [];
                    }
                    headersByRoute[this.routes[r].order].push({
                      routeIds: [this.routes[r].id],
                      shiftId: this.shifts[s].id,
                      label: this.shifts[s].name,
                      availability: availability
                    });
                  } else {
                    this.tableData[period][w][d][
                      this.routes[r].id + "|" + this.shifts[s].id
                    ] = {
                      label: this.shifts[s].name,
                      availability: availability
                    };

                    this.tableDataChanges[period][w][d][
                      this.routes[r].id + "|" + this.shifts[s].id
                    ] = availability;
                  }
                } else {
                  mergeRoutes.push({
                    routeId: this.routes[r].id,
                    shiftId: this.shifts[s].id,
                    label: this.shifts[s].name,
                    availability: availability
                  });
                }
              }

              if (mergeRoutes.length > 0) {
                let routeIds = [];
                for (let m in mergeRoutes) {
                  routeIds.push(mergeRoutes[m].routeId);
                }

                if (this.orderAvailabilityByRoute === true) {
                  if (headersByRoute[0] === undefined) {
                    headersByRoute[0] = [];
                  }
                  headersByRoute[0].push({
                    routeIds: routeIds,
                    shiftId: mergeRoutes[0].shiftId,
                    label: mergeRoutes[0].label,
                    availability: mergeRoutes[0].availability
                  });
                } else {
                  this.tableData[period][w][d][
                    routeIds.toString() + "|" + mergeRoutes[0].shiftId
                  ] = {
                    label: mergeRoutes[0].label,
                    availability: mergeRoutes[0].availability
                  };

                  this.tableDataChanges[period][w][d][
                    routeIds.toString() + "|" + mergeRoutes[0].shiftId
                  ] = mergeRoutes[0].availability;

                  mergeRoutes = [];
                }
              }
            }

            if (this.orderAvailabilityByRoute === true) {
              for (let o in headersByRoute) {
                for (let i in headersByRoute[o]) {
                  if (this.tableData[period] === undefined) {
                    this.tableData[period] = {};
                  }
                  if (this.tableData[period][w] === undefined) {
                    this.tableData[period][w] = {};
                  }
                  if (this.tableData[period][w][d] === undefined) {
                    this.tableData[period][w][d] = {};
                  }
                  this.tableData[period][w][d][
                    headersByRoute[o][i].routeIds.toString() +
                      "|" +
                      headersByRoute[o][i].shiftId
                  ] = {
                    label: headersByRoute[o][i].label,
                    availability: headersByRoute[o][i].availability
                  };

                  this.tableDataChanges[period][w][d][
                    headersByRoute[o][i].routeIds.toString() +
                      "|" +
                      headersByRoute[o][i].shiftId
                  ] = headersByRoute[o][i].availability;
                }
              }
            }
          }
        }
      }

      this.formDataChanges[period] = {};
      if (this.stopDate === null) {
        this.formDataChanges[period].startDate = null;
        this.startDate = null;
      } else {
        let stopDate = new Date(this.formatDateBack(this.stopDate));

        this.formDataChanges[period].startDate =
          stopDate.addDays(1).getUTCDate() +
          "-" +
          (stopDate.addDays(1).getUTCMonth() + 1) +
          "-" +
          stopDate.addDays(1).getUTCFullYear();
        this.startDate = this.formDataChanges[period].startDate;
      }
      this.formDataChanges[period].stopDate = null;
      this.stopDate = null;

      this.touched.tableData = true;

      this.selectedPeriod = period;
    },
    handleDelete(selectedPeriod) {
      sweetAlert
        .fire({
          title: "Waarschuwing!",
          text: "Weet u zeker dat u de geselecteerde periode wilt verwijderen?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Ja, verwijder de periode",
          cancelButtonText: "Annuleren",
          customClass: {
            confirmButton: "md-button md-warning",
            cancelButton: "md-button md-success"
          },
          buttonsStyling: false
        })
        .then(result => {
          if (result.value) {
            this.deletePeriod(selectedPeriod);
          } else if (result.dismiss === sweetAlert.DismissReason.cancel) {
          }
        });
    },
    deletePeriod(selectedPeriod) {
      let periodIds = [];

      let startDate = this.getStartDateFromPeriod(selectedPeriod);
      let stopDate = this.getStopDateFromPeriod(selectedPeriod);

      let newAvailablePeriods = [];
      for (let p in this.availablePeriods) {
        if (
          this.availablePeriods[p].startDate !== startDate ||
          this.availablePeriods[p].stopDate !== stopDate
        ) {
          newAvailablePeriods.push(this.availablePeriods[p]);
          continue;
        }

        periodIds.push(this.availablePeriods[p].id);
      }
      this.availablePeriods = newAvailablePeriods;

      this.requesting = true;
      this.$http
        .delete(this.$apiBaseUrl + "drivers/availableperiods", {
          body: { ids: periodIds }
        })
        .then(
          response => {
            this.requesting = false;
            this.notifyVue(
              "Beschikbaarheid succesvol verwijderd",
              "check",
              "success",
              5000
            );
            this.resetAfterDelete();
          },
          response => {
            if (response.status === 422) {
              this.requesting = false;
              let msg =
                "Fout geconstateerd tijdens het verwijderen van beschikbaarheid:";

              for (var i in response.data.errors) {
                msg += "<br/>\n&bull;&nbsp;" + response.data.errors[i];
              }

              this.notifyVue(msg, "add_alert", "danger", 0);
            } else {
              this.notifyVue(
                "Onbekende fout tijdens opslaan van beschikbaarheid voor " +
                  this.getDayOfTheWeek(data[dataIndex].dayOfTheWeek),
                "report_problem",
                "danger",
                0
              );
            }
          }
        );
    },
    getIconByStatus(status) {
      return this.selectableAvailabilityTypes[status].icon;
    },
    getLabelByStatus(status) {
      return this.selectableAvailabilityTypes[status].label;
    },
    getButtonBackgroundColorByStatus(status) {
      return this.selectableAvailabilityTypes[status].color;
    },
    getIconStyleColorByStatus(status) {
      return (
        "color: " +
        this.selectableAvailabilityTypes[status].color +
        " !important;"
      );
    },
    getButtonStyleColorByStatus(status) {
      return (
        "border: 3px solid " +
        this.selectableAvailabilityTypes[status].color +
        ";"
      );
    },
    notifyVue(message, icon, type, timeout) {
      this.$notify({
        message: message,
        icon: icon,
        horizontalAlign: "center",
        verticalAlign: "top",
        type: type,
        timeout: timeout
      });
    },
    changeSelectedPeriod(period) {
      if (this.selectedPeriod === period) {
        return;
      }

      this.selectedPeriod = period;

      this.requesting = true;
      this.startDate =
        this.formDataChanges[this.selectedPeriod] &&
        this.formDataChanges[this.selectedPeriod]["startDate"]
          ? this.formatDate(
              this.formDataChanges[this.selectedPeriod]["startDate"]
            )
          : this.formatDate(this.getStartDateFromSelectedPeriod);
      this.stopDate =
        this.formDataChanges[this.selectedPeriod] &&
        this.formDataChanges[this.selectedPeriod]["stopDate"]
          ? this.formatDate(
              this.formDataChanges[this.selectedPeriod]["stopDate"]
            )
          : this.formatDate(this.getStopDateFromSelectedPeriod);
      setTimeout(
        function() {
          this.requesting = false;
        }.bind(this),
        100
      );
    },
    getPeriodLabel(period) {
      let label = "";
      period.split("|").forEach(period => {
        if (period === "null" && label === "") {
          label += "Begin - ";
        } else if (period === "new" && label === "") {
          label += "Nieuw - ";
        } else if (period === "null") {
          label += " Einde";
        } else if (period === "new") {
          label += " Nieuw";
        } else if (label === "") {
          label += this.formatDate(period) + " - ";
        } else {
          label += this.formatDate(period);
        }
      });

      return label;
    },
    calculateTableData() {
      this.tableData = {};
      this.tableDataChanges = {};
      for (let p in this.dataUniquePeriods) {
        let startDate = this.getStartDateFromPeriod(this.dataUniquePeriods[p]);
        let stopDate = this.getStopDateFromPeriod(this.dataUniquePeriods[p]);
        this.tableData[this.dataUniquePeriods[p]] = {};
        for (let w in this.getWeeksOddEven()) {
          this.tableData[this.dataUniquePeriods[p]][w] = {};
          for (let d in this.daysOfTheWeek) {
            let routesShiftsCounter = 0;
            let routesCounter = 0;
            this.tableData[this.dataUniquePeriods[p]][w][d] = {};
            for (let g in this.groups) {
              let headersByRoute = {};
              let shiftsCounter = 0;
              for (let s in this.shifts) {
                let mergeRoutes = [];

                if (this.orderAvailabilityByRoute === false) {
                  routesCounter = 0;
                }

                for (let r in this.routes) {
                  if (
                    this.routes[r].routegroups_id !== this.groups[g].id ||
                    this.shifts[s].routeIds.indexOf(this.routes[r].id) === -1
                  ) {
                    continue;
                  }

                  if (
                    this.groupsToMergeRoutes.indexOf(this.groups[g].id) ===
                      -1 &&
                    this.routesToMerge.indexOf(this.routes[r].id) === -1
                  ) {
                    if (this.orderAvailabilityByRoute === true) {
                      if (headersByRoute[this.routes[r].order] === undefined) {
                        headersByRoute[this.routes[r].order] = [];
                      }
                      headersByRoute[this.routes[r].order].push({
                        routeIds: [this.routes[r].id],
                        shiftId: this.shifts[s].id,
                        label: this.shifts[s].name,
                        availability: this.getAvailability(
                          startDate,
                          stopDate,
                          parseInt(w),
                          parseInt(d),
                          this.shifts[s].id,
                          [this.routes[r].id]
                        ),
                        class:
                          headersByRoute[this.routes[r].order].length === 0 &&
                          routesCounter > 1 &&
                          shiftsCounter === 1
                            ? "contrast c1 rs" +
                              routesShiftsCounter +
                              " r" +
                              routesCounter +
                              " s" +
                              shiftsCounter
                            : "c1 rs" +
                              routesShiftsCounter +
                              " r" +
                              routesCounter +
                              " s" +
                              shiftsCounter
                      });
                    } else {
                      routesCounter++;
                      if (routesCounter === 1) {
                        shiftsCounter++;
                        routesShiftsCounter++;
                      }
                      this.tableData[this.dataUniquePeriods[p]][w][d][
                        this.routes[r].id + "|" + this.shifts[s].id
                      ] = {
                        label: this.shifts[s].name,
                        availability: this.getAvailability(
                          startDate,
                          stopDate,
                          parseInt(w),
                          parseInt(d),
                          this.shifts[s].id,
                          [this.routes[r].id]
                        ),
                        class:
                          routesShiftsCounter > 1 &&
                          routesCounter === 1 &&
                          shiftsCounter > 1
                            ? "contrast c2 rs" +
                              routesShiftsCounter +
                              " r" +
                              routesCounter +
                              " s" +
                              shiftsCounter
                            : "c2 rs" +
                              routesShiftsCounter +
                              " r" +
                              routesCounter +
                              " s" +
                              shiftsCounter
                      };
                    }
                  } else {
                    if (mergeRoutes.length === 0) {
                      // create placeholder to keep the correct order
                      this.tableData[this.dataUniquePeriods[p]][w][d][
                        this.routes[r].id + "|" + this.shifts[s].id
                      ] = {
                        routeId: this.routes[r].id,
                        shiftId: this.shifts[s].id,
                        label: "PLACEHOLDER",
                        availability: 2,
                        class: "placeholder"
                      };
                    }
                    if (
                      this.orderAvailabilityByRoute === false &&
                      mergeRoutes.length === 0
                    ) {
                      routesCounter++;
                      if (routesCounter === 1) {
                        shiftsCounter++;
                        routesShiftsCounter++;
                      }
                    }
                    mergeRoutes.push({
                      routeId: this.routes[r].id,
                      shiftId: this.shifts[s].id,
                      label: this.shifts[s].name,
                      availability: this.getAvailability(
                        startDate,
                        stopDate,
                        parseInt(w),
                        parseInt(d),
                        this.shifts[s].id,
                        [this.routes[r].id]
                      ),
                      class:
                        "contrast c3 rs" +
                        routesShiftsCounter +
                        " r" +
                        routesCounter +
                        " s" +
                        shiftsCounter
                    });
                  }
                }

                if (mergeRoutes.length > 0) {
                  let routeIds = [];
                  for (let m in mergeRoutes) {
                    routeIds.push(mergeRoutes[m].routeId);
                  }

                  if (this.orderAvailabilityByRoute === true) {
                    if (headersByRoute[0] === undefined) {
                      headersByRoute[0] = [];
                    }
                    headersByRoute[0].push({
                      routeIds: routeIds,
                      shiftId: mergeRoutes[0].shiftId,
                      label: mergeRoutes[0].label,
                      availability: mergeRoutes[0].availability,
                      class: "c4 " + mergeRoutes[0].class
                    });
                  } else {
                    // Rename placeholder to correct name so the dats below is correctly written to the right array key and maintaining the correct order
                    if (
                      this.tableData[this.dataUniquePeriods[p]][w][d][
                        mergeRoutes[0].routeId + "|" + mergeRoutes[0].shiftId
                      ] !== undefined
                    ) {
                      this.tableData[this.dataUniquePeriods[p]][w][
                        d
                      ] = this.UpdateKey(
                        this.tableData[this.dataUniquePeriods[p]][w][d],
                        mergeRoutes[0].routeId + "|" + mergeRoutes[0].shiftId,
                        routeIds.toString() + "|" + mergeRoutes[0].shiftId
                      );
                    }
                    this.tableData[this.dataUniquePeriods[p]][w][d][
                      routeIds.toString() + "|" + mergeRoutes[0].shiftId
                    ] = {
                      label: mergeRoutes[0].label,
                      availability: mergeRoutes[0].availability,
                      class: "c5 " + mergeRoutes[0].class
                    };

                    mergeRoutes = [];
                  }
                }
              }
              if (this.orderAvailabilityByRoute === true) {
                for (let o in headersByRoute) {
                  routesShiftsCounter++;
                  routesCounter++;
                  shiftsCounter = 0;
                  for (let i in headersByRoute[o]) {
                    shiftsCounter++;

                    // Rename placeholder to correct name so the dats below is correctly written to the right array key and maintaining the correct order
                    if (
                      this.tableData[this.dataUniquePeriods[p]][w][d][
                        headersByRoute[o][i].routeIds[0] +
                          "|" +
                          headersByRoute[o][i].shiftId
                      ] !== undefined
                    ) {
                      this.tableData[this.dataUniquePeriods[p]][w][
                        d
                      ] = this.UpdateKey(
                        this.tableData[this.dataUniquePeriods[p]][w][d],
                        headersByRoute[o][i].routeIds[0] +
                          "|" +
                          headersByRoute[o][i].shiftId,
                        headersByRoute[o][i].routeIds.toString() +
                          "|" +
                          headersByRoute[o][i].shiftId
                      );
                    }

                    this.tableData[this.dataUniquePeriods[p]][w][d][
                      headersByRoute[o][i].routeIds.toString() +
                        "|" +
                        headersByRoute[o][i].shiftId
                    ] = {
                      label: headersByRoute[o][i].label,
                      availability: headersByRoute[o][i].availability,
                      class:
                        routesShiftsCounter > 1 &&
                        routesCounter > 1 &&
                        shiftsCounter === 1
                          ? "contrast c6 rs" +
                            routesShiftsCounter +
                            " r" +
                            routesCounter +
                            " s" +
                            shiftsCounter
                          : "c6 rs" +
                            routesShiftsCounter +
                            " r" +
                            routesCounter +
                            " s" +
                            shiftsCounter
                    };
                  }
                }
              }
            }
          }
        }
      }
    },
    loopThroughStatusCodes(
      weekOddEvenIndex,
      tableRowIndex,
      tableRowColumnIndex
    ) {
      let found = false;
      if (
        this.tableData[this.selectedPeriod][weekOddEvenIndex][tableRowIndex][
          tableRowColumnIndex
        ].availability === 9
      ) {
        found = true;
      }
      let newAvailability = 9;
      for (let t in this.selectableAvailabilityTypes) {
        if (found === true) {
          this.tableData[this.selectedPeriod][weekOddEvenIndex][tableRowIndex][
            tableRowColumnIndex
          ].availability = parseInt(t);
          newAvailability = parseInt(t);
          break;
        }

        if (
          this.tableData[this.selectedPeriod][weekOddEvenIndex][tableRowIndex][
            tableRowColumnIndex
          ].availability === parseInt(t)
        ) {
          found = true;
        }
      }

      if (!(this.selectedPeriod in this.tableDataChanges)) {
        this.tableDataChanges[this.selectedPeriod] = {};
      }
      if (!(weekOddEvenIndex in this.tableDataChanges[this.selectedPeriod])) {
        this.tableDataChanges[this.selectedPeriod][weekOddEvenIndex] = {};
      }
      if (
        !(
          tableRowIndex in
          this.tableDataChanges[this.selectedPeriod][weekOddEvenIndex]
        )
      ) {
        this.tableDataChanges[this.selectedPeriod][weekOddEvenIndex][
          tableRowIndex
        ] = {};
      }
      if (
        !(
          tableRowColumnIndex in
          this.tableDataChanges[this.selectedPeriod][weekOddEvenIndex][
            tableRowIndex
          ]
        )
      ) {
        this.tableDataChanges[this.selectedPeriod][weekOddEvenIndex][
          tableRowIndex
        ][tableRowColumnIndex] = "";
      }

      this.tableDataChanges[this.selectedPeriod][weekOddEvenIndex][
        tableRowIndex
      ][tableRowColumnIndex] = newAvailability;

      this.touched.tableData = true;
      this.$forceUpdate();
    },
    getStartDateFromPeriod(period) {
      return period.split("|")[0] === "null" || period.split("|")[0] === "new"
        ? null
        : this.formatDateBack(period.split("|")[0]);
    },
    getStopDateFromPeriod(period) {
      return period.split("|")[1] === "null" || period.split("|")[1] === "new"
        ? null
        : this.formatDateBack(period.split("|")[1]);
    },
    resetTouched() {
      this.touched.startDate = false;
      this.touched.stopDate = false;
      this.touched.tableData = false;
    },
    showSweetAlertGoBack() {
      sweetAlert
        .fire({
          title: "Waarschuwing!",
          text: "Weet u zeker dat u de wijzigingen wilt negeren?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Ja, negeer de wijzigingen",
          cancelButtonText: "Annuleren",
          customClass: {
            confirmButton: "md-button md-warning",
            cancelButton: "md-button md-success"
          },
          buttonsStyling: false
        })
        .then(result => {
          if (result.value) {
            this.$router.go(-1);
          } else if (result.dismiss === sweetAlert.DismissReason.cancel) {
          }
        });
    },
    getDataUniquePeriods() {
      let periods = [];

      if (this.availablePeriods === undefined) {
        return periods;
      }

      for (var i in this.availablePeriods) {
        if (this.availablePeriods[i].id.length !== 36) {
          continue;
        }

        if (
          periods.indexOf(
            this.availablePeriods[i].startDate +
              "|" +
              this.availablePeriods[i].stopDate
          ) !== -1
        ) {
          continue;
        }

        let period =
          this.availablePeriods[i].startDate +
          "|" +
          this.availablePeriods[i].stopDate;

        periods.push(period);
      }

      if (this.tableData["new|new"] !== undefined) {
        periods.push("new|new");
      }

      return periods.sort((a, b) => {
        if (a.substring(0, 4) === "null") {
          return -1;
        } else if (b.substring(0, 4) === "null") {
          return 1;
        }

        if (a > b) {
          return 1;
        } else if (a < b) {
          return -1;
        }
        return 0;
      });
    }
  },
  watch: {
    dataUniquePeriods() {
      if (this.selectedPeriod === null && this.dataUniquePeriods.length > 0) {
        this.selectedPeriod = this.dataUniquePeriods[
          this.dataUniquePeriods.length - 1
        ];

        this.requesting = true;
        this.startDate = this.formatDate(this.getStartDateFromSelectedPeriod);
        this.stopDate = this.formatDate(this.getStopDateFromSelectedPeriod);
        setTimeout(
          function() {
            this.requesting = false;
          }.bind(this),
          100
        );
      }
    },
    everythingLoaded() {
      if (this.everythingLoaded) {
        this.calculateTableData();
        setTimeout(this.resetTouched, 100);
      }
    },
    startDate() {
      if (!this.requesting) {
        this.touched.startDate = true;
        if (this.formDataChanges[this.selectedPeriod] === undefined) {
          this.formDataChanges[this.selectedPeriod] = {};
        }
        this.formDataChanges[this.selectedPeriod][
          "startDate"
        ] = this.formatDateBack(this.startDate);
      }
    },
    stopDate() {
      if (!this.requesting) {
        this.touched.stopDate = true;
        if (this.formDataChanges[this.selectedPeriod] === undefined) {
          this.formDataChanges[this.selectedPeriod] = {};
        }
        this.formDataChanges[this.selectedPeriod][
          "stopDate"
        ] = this.formatDateBack(this.stopDate);
      }
    },
    orderAvailabilityByRoute() {
      this.calculateTableData();
      setTimeout(this.resetTouched, 100);
      this.tableDataChanges = {};
    },
    tableData: {
      handler: function(val, oldVal) {
        this.touched.tableData = true;
      },
      deep: true
    }
  },
  mounted() {
    this.init();
    this.loadRouteGroups();
    this.loadRoutes();
    this.loadShifts();
    this.loadDriver(this.$route.params.userId);
    this.loadAvailablePeriods(this.$route.params.userId);
    this.loadSelectableAvailabilityTypes();
  }
};
</script>

<style lang="css" scoped>
ul.legend {
  list-style-type: none;
}

div.alert.alert-success {
  margin-top: 1em;
}

div.scrollContainer {
  display: inline-block;
  overflow-x: auto;
  max-width: 100%;
  padding-bottom: 12px;

  table {
    margin-bottom: 1em;
  }
}

table.striped {
  tr th {
    vertical-align: bottom;

    hr {
      font-size: 1px;
    }
  }

  tr:nth-child(even) th:not(:first-child) {
    border-left: 1px solid #dddddd;
  }

  tr:nth-child(odd) {
    background-color: #f5f5f5;

    .availability-status {
      background-color: #f5f5f5 !important;
    }
  }

  .availability-button .md-ripple {
    padding: 0.3rem !important;
    background-color: #eee !important;
  }

  .availability-button .md-ripple .md-button-content {
    background-color: #eee !important;
    color: #333 !important;
  }

  .availability-status {
    cursor: pointer;
    border: 0;
    border-radius: 13px;
    background-color: #fff;
  }

  &.contrast {
    tr th.contrast,
    tr td.contrast {
      border-left: 3px dotted #999;
      padding-left: 5px;
    }
  }
}

.md-card .md-card-actions {
  border: none;
}

.md-card-content > span > .md-field.md-datepicker {
  padding-top: 15px;

  &:after {
    bottom: 20px;
  }
}

.md-inline-checkboxes .md-checkbox .md-checkbox-label {
  line-height: 6px !important;
}
</style>
